import { createContext, useContext, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { MeResponse, User } from "@pinpoint/shared";
import { apiFetch, ApiError } from "./api";

const ME_KEY = ["auth", "me"] as const;

interface AuthValue {
  user: User | null;
  isLoading: boolean;
  /** Set when access was refused with a reason worth showing the user. */
  accessError: string | null;
  signIn: () => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: () => apiFetch<MeResponse>("/api/auth/me"),
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  const signOut = useMutation({
    mutationFn: () => apiFetch("/api/auth/logout", { method: "POST" }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ME_KEY }),
  });

  const signIn = () => {
    // Full-page navigation, not fetch: Google's consent screen must be a
    // top-level document, and the session cookie is set on the redirect back.
    const returnTo = window.location.pathname + window.location.search;
    window.location.href = `/auth/google/start?return_to=${encodeURIComponent(returnTo)}`;
  };

  const error = me.error;
  const accessError =
    error instanceof ApiError && error.status === 403 ? error.message : null;

  const value: AuthValue = {
    user: me.data?.authenticated ? me.data.user : null,
    isLoading: me.isPending,
    accessError,
    signIn,
    signOut: () => signOut.mutate(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
